import { useEffect, useRef, useState } from 'react';

interface ServiceCardProps {
  image: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  delay: number;
}

function ServiceCard({ image, icon, title, subtitle, description, delay }: ServiceCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms, box-shadow 0.3s ease, translate 0.3s ease`,
      }}
    >
      <div className="w-full h-48 overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 flex items-center justify-center bg-green-50 text-green-600 rounded-md">
            <i className={`${icon} text-xl`} />
          </div>
          <div>
            <h4 className="font-bold text-gray-800 text-base">{title}</h4>
            <p className="text-green-600 text-xs font-medium">{subtitle}</p>
          </div>
        </div>
        <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

const services = [
  {
    image:
      'https://readdy.ai/api/search-image?query=professional%20mechanic%20inspecting%20a%20car%20on%20a%20hydraulic%20lift%20during%20vehicle%20inspection%2C%20clean%20well-lit%20garage%2C%20mechanic%20in%20dark%20blue%20uniform%20using%20diagnostic%20tools%2C%20organized%20professional%20workshop%2C%20white%20ceiling%2C%20concrete%20floor%2C%20realistic%20photography&width=800&height=600&seq=svc001&orientation=landscape',
    icon: 'ri-search-2-line',
    title: '車検・一般整備',
    subtitle: 'Inspection & Maintenance',
    description:
      '国が定めた保安基準に沿った車検整備を実施します。定期点検・オイル交換など日常的なメンテナンスにも対応しています。',
    delay: 0,
  },
  {
    image:
      'https://readdy.ai/api/search-image?query=skilled%20auto%20body%20technician%20carefully%20repairing%20car%20dent%20and%20scratch%20with%20professional%20tools%2C%20clean%20body%20shop%2C%20white%20background%20walls%2C%20technician%20in%20uniform%2C%20spray%20painting%20equipment%20visible%2C%20professional%20auto%20body%20repair%20shop%2C%20high%20quality%20realistic%20photo&width=800&height=600&seq=svc002&orientation=landscape',
    icon: 'ri-hammer-line',
    title: '板金・塗装',
    subtitle: 'Body Repair & Painting',
    description:
      '小さなキズや軽微なへこみから、大きなダメージの修復まで対応します。丁寧な下地処理と色合わせで、元の状態に近づけます。',
    delay: 100,
  },
  {
    image:
      'https://readdy.ai/api/search-image?query=mechanic%20mounting%20new%20tire%20on%20wheel%20balancer%20machine%20in%20bright%20clean%20tire%20shop%2C%20stacked%20tires%20in%20background%2C%20professional%20tire%20service%2C%20clean%20organized%20workshop%2C%20mechanic%20in%20uniform%2C%20rubber%20tires%20various%20sizes%2C%20realistic%20photography&width=800&height=600&seq=svc003&orientation=landscape',
    icon: 'ri-settings-3-line',
    title: 'タイヤ交換・販売',
    subtitle: 'Tire Exchange & Sales',
    description:
      '各メーカーのタイヤ販売および交換作業を承ります。お客様の持ち込みタイヤへの交換にも対応しています。',
    delay: 200,
  },
  {
    image:
      'https://readdy.ai/api/search-image?query=experienced%20mechanic%20working%20on%20car%20engine%20compartment%20with%20professional%20tools%2C%20clean%20organized%20auto%20repair%20workshop%2C%20engine%20hood%20open%2C%20various%20diagnostic%20equipment%2C%20bright%20lighting%2C%20mechanic%20in%20uniform%2C%20realistic%20photography&width=800&height=600&seq=svc004&orientation=landscape',
    icon: 'ri-tools-line',
    title: '一般修理',
    subtitle: 'General Repair',
    description:
      'エンジン不調・エアコン修理・ブレーキ整備・オイル交換など、各種修理・点検に対応します。お気軽にご相談ください。',
    delay: 300,
  },
];

export default function ServicesSection() {
  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-14">
          <span className="text-green-600 text-sm font-semibold tracking-widest uppercase">Services</span>
          <h2 className="text-3xl font-bold text-gray-800 mt-2">対応サービス一覧</h2>
          <div className="w-12 h-1 bg-green-600 mx-auto mt-4 rounded-full" />
          <p className="text-gray-500 mt-4 text-sm leading-relaxed max-w-xl mx-auto">
            車検から板金・タイヤ・修理まで、お車のことならなんでもご相談ください。
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s) => (
            <ServiceCard key={s.title} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}
